/**
 * Typed API client.
 *
 * Every call carries an Entra ID bearer token acquired for this user. There is
 * no shared API key.
 */
import { acquireApiToken } from '../auth/msal';
import { config } from '../config';
import type {
  ApplicantSearchResult,
  AppUser,
  AssignableUser,
  AuditLogEntry,
  Complaint,
  ComplaintDetail,
  ComplaintListResponse,
  CurrentUser,
  EmailLogEntry,
  EmailRule,
  EmailTemplate,
  EmailTrigger,
  HardshipFilter,
  MaxDaysMode,
  ReferenceData,
  ReportsOverview,
  Role,
  ValidationDetail,
} from './types';

/** MIME types the API will accept — kept in sync with api/src/domain/attachment.ts. */
export const ALLOWED_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
    readonly details?: ValidationDetail[]
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller is authenticated but not registered or deactivated. */
  get isAccessDenied(): boolean {
    return this.status === 403;
  }

  /** True when someone else changed the record first. */
  get isStale(): boolean {
    return this.status === 412 || this.status === 409;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await acquireApiToken();

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (payload as { error?: string }).error ?? `Request failed (${response.status})`,
      (payload as { code?: string }).code ?? 'unknown',
      (payload as { details?: ValidationDetail[] }).details
    );
  }

  return payload as T;
}

type QueryValue = string | number | boolean | undefined;

function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export type ComplaintSortColumn =
  | 'reference'
  | 'complainant'
  | 'application_loan'
  | 'date_received'
  | 'idr_due'
  | 'status'
  | 'lodged_by'
  | 'assigned_to';

export interface ComplaintFilters extends Record<string, QueryValue> {
  search?: string;
  status?: string;
  lodged_by?: string;
  assigned_to?: string;
  overdue?: boolean;
  sensitive?: boolean;
  idr_due_from?: string;
  idr_due_to?: string;
  sort?: ComplaintSortColumn;
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const api = {
  me: () => request<CurrentUser>('/me'),

  reference: () => request<ReferenceData>('/reference'),

  reportsOverview: () => request<ReportsOverview>('/reports/overview'),

  listComplaints: (filters: ComplaintFilters = {}) =>
    request<ComplaintListResponse>(`/complaints${queryString(filters)}`),

  getComplaint: (id: number) => request<ComplaintDetail>(`/complaints/${id}`),

  addNote: (complaintId: number, note: string) =>
    request<{ id: number }>(`/complaints/${complaintId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  uploadAttachment: (complaintId: number, file: File, note?: string) =>
    request<{ id: number }>(`/complaints/${complaintId}/attachments`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
        'X-File-Name': encodeURIComponent(file.name),
        ...(note ? { 'X-Attachment-Note': encodeURIComponent(note) } : {}),
      },
    }),

  updateAttachmentNote: (complaintId: number, attachmentId: number, note: string) =>
    request<void>(`/complaints/${complaintId}/attachments/${attachmentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    }),

  getAttachmentDownloadUrl: (complaintId: number, attachmentId: number, disposition: 'inline' | 'attachment' = 'attachment') =>
    request<{ url: string }>(
      `/complaints/${complaintId}/attachments/${attachmentId}/download${queryString({ disposition })}`
    ),

  deleteAttachment: (complaintId: number, attachmentId: number) =>
    request<void>(`/complaints/${complaintId}/attachments/${attachmentId}`, { method: 'DELETE' }),

  createComplaint: (input: Record<string, unknown>) =>
    request<{ id: number; reference: string }>('/complaints', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  searchApplicantProfiles: (q: string) =>
    request<{ items: ApplicantSearchResult[] }>(`/complaint-lookup${queryString({ q })}`),

  updateComplaint: (id: number, input: Partial<Complaint> & { row_version: number }) =>
    request<{ row_version: number }>(`/complaints/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  listUsers: () => request<{ items: AppUser[] }>('/users'),

  listAssignableUsers: () => request<{ items: AssignableUser[] }>('/assignable-users'),

  createUser: (input: { email: string; display_name?: string; role: Role }) =>
    request<{ id: number }>('/users', { method: 'POST', body: JSON.stringify(input) }),

  updateUser: (id: number, input: { role?: Role; is_active?: boolean }) =>
    request<{ id: number }>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  listEmailTemplates: () => request<{ items: EmailTemplate[] }>('/email/templates'),

  createEmailTemplate: (input: { name: string; subject: string; body: string; default_to?: string; is_active?: boolean }) =>
    request<{ id: number }>('/email/templates', { method: 'POST', body: JSON.stringify(input) }),

  updateEmailTemplate: (
    id: number,
    input: Partial<{ name: string; subject: string; body: string; default_to: string | null; is_active: boolean }>
  ) => request<{ id: number }>(`/email/templates/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  listEmailRules: () => request<{ items: EmailRule[] }>('/email/rules'),

  createEmailRule: (input: {
    name: string;
    trigger_on: EmailTrigger;
    template_id: number;
    to_override?: string;
    enabled?: boolean;
    hardship_filter?: HardshipFilter;
    min_days_after_received?: number;
    max_days_mode?: MaxDaysMode;
    max_days_after_received?: number;
    outcome_codes?: number[];
  }) => request<{ id: number }>('/email/rules', { method: 'POST', body: JSON.stringify(input) }),

  updateEmailRule: (
    id: number,
    input: Partial<{
      name: string;
      trigger_on: EmailTrigger;
      template_id: number;
      to_override: string | null;
      enabled: boolean;
      hardship_filter: HardshipFilter;
      min_days_after_received: number | null;
      max_days_mode: MaxDaysMode;
      max_days_after_received: number | null;
      outcome_codes: number[] | null;
    }>
  ) => request<{ id: number }>(`/email/rules/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  listEmailLog: () => request<{ items: EmailLogEntry[] }>('/email/log'),

  listAuditLog: (search?: string) =>
    request<{ items: AuditLogEntry[] }>(`/audit${queryString({ search })}`),
};
